import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { FilterDefinition } from '@/components/ui/custom/ComplexFilter/ComplexFilterCombobox';
import { FilterItem } from '@/components/ui/custom/ComplexFilter/types';
import { getFilterValues } from '@/hooks/useTableFilters';

interface UseCampaignLogsFilterBadgesParams {
  filters: FilterItem[];
  filterDefinitions: FilterDefinition[];
  setFilter: (key: string, values: string[], exclude?: boolean) => void;
  removeFilter: (...keys: string[]) => void;
}

export const useCampaignLogsFilterBadges = ({
  filters,
  filterDefinitions,
  setFilter,
  removeFilter,
}: UseCampaignLogsFilterBadgesParams) => {
  const { t } = useTranslation();

  return useMemo(() => {
    const badges = [];

    const errorTypes = getFilterValues(filters, 'error_type');
    if (errorTypes.length > 0) {
      const def = filterDefinitions.find((f) => f.id === 'error_type')!;
      badges.push({
        id: 'error_type',
        icon: def.icon,
        title: t('campaigns.logs_filters.error_type'),
        text: errorTypes[0],
        onRemove: () => removeFilter('error_type'),
        filterDefinition: { type: def.type, options: [], placeholder: def.placeholder },
        value: errorTypes[0],
        onValueChange: (value) =>
          value ? setFilter('error_type', [value as string]) : removeFilter('error_type'),
      });
    }

    const channels = getFilterValues(filters, 'channels');
    if (channels.length > 0) {
      const def = filterDefinitions.find((f) => f.id === 'channels')!;
      const firstLabel = def.options.find((o) => o.value === channels[0])?.label ?? channels[0];
      badges.push({
        id: 'channels',
        icon: def.icon,
        title: t('common.channels'),
        text: channels.length === 1 ? firstLabel : `${firstLabel} +${channels.length - 1}`,
        onRemove: () => removeFilter('channels'),
        filterDefinition: { type: def.type, options: def.options },
        value: channels,
        onValueChange: (value) =>
          value && (value as string[]).length > 0
            ? setFilter('channels', value as string[])
            : removeFilter('channels'),
      });
    }

    const statuses = getFilterValues(filters, 'status');
    if (statuses.length > 0) {
      const def = filterDefinitions.find((f) => f.id === 'status')!;
      const statusLabel = def.options.find((o) => o.value === statuses[0])?.label ?? statuses[0];
      badges.push({
        id: 'status',
        icon: def.icon,
        title: t('common.status'),
        text: statusLabel,
        onRemove: () => removeFilter('status'),
        filterDefinition: { type: def.type, options: def.options },
        value: statuses[0],
        onValueChange: (value) =>
          value ? setFilter('status', [value as string]) : removeFilter('status'),
      });
    }

    const repliedValues = getFilterValues(filters, 'replied');
    if (repliedValues.length > 0) {
      const def = filterDefinitions.find((f) => f.id === 'replied')!;
      const repliedLabel =
        def.options.find((o) => o.value === repliedValues[0])?.label ?? repliedValues[0];
      badges.push({
        id: 'replied',
        icon: def.icon,
        title: t('broadcasts.recipients_status.replied'),
        text: repliedLabel,
        onRemove: () => removeFilter('replied'),
        filterDefinition: { type: def.type, options: def.options },
        value: repliedValues[0],
        onValueChange: (value) =>
          value ? setFilter('replied', [value as string]) : removeFilter('replied'),
      });
    }

    return badges;
  }, [filters, filterDefinitions, removeFilter, setFilter, t]);
};
